import { useState, useEffect, useCallback, useRef } from 'react';
import { API, formatWeight, displayDate } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area, ComposedChart } from 'recharts';
import { TrendingUp, TrendingDown, BarChart3, Package, Truck, ArrowUpDown, CalendarDays, Search, Check, X } from 'lucide-react';
import { format, subDays } from 'date-fns';

const COLORS = ['hsl(40, 89%, 45%)', 'hsl(160, 45%, 34%)', 'hsl(220, 32%, 32%)', 'hsl(28, 85%, 54%)', 'hsl(0, 72%, 51%)'];
// Fixed color per purity so both pie charts match
const PURITY_COLORS = { '76': 'hsl(40, 89%, 45%)', '84': 'hsl(160, 45%, 34%)', '92': 'hsl(220, 50%, 50%)', 'Unknown': 'hsl(0, 0%, 45%)' };
const getPurityColor = (purity) => PURITY_COLORS[String(purity)] || COLORS[0];
const chartTickStyle = { fontSize: 10, fill: 'hsl(222, 10%, 42%)' };
const axisStroke = 'hsl(36, 18%, 86%)';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-card border border-border rounded-lg shadow-md p-2 text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: <span className="font-mono">{typeof p.value === 'number' ? p.value.toFixed(3) : p.value}</span>
        </p>
      ))}
    </div>
  );
};

function toISO(date) {
  return format(date, 'yyyy-MM-dd');
}

function DateRangePicker({ dateRange, onDateRangeChange }) {
  const [open, setOpen] = useState(false);
  const [pendingRange, setPendingRange] = useState(undefined);
  const clickCount = useRef(0);

  const handleOpen = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      clickCount.current = 0;
      setPendingRange(undefined);
    }
  };

  const handleSelect = (range) => {
    clickCount.current += 1;
    setPendingRange(range);
    if (clickCount.current >= 2 && range?.from && range?.to) {
      onDateRangeChange(range);
      setOpen(false);
      clickCount.current = 0;
      setPendingRange(undefined);
    }
  };

  const displayRange = open ? pendingRange : dateRange;
  const rangeLabel = dateRange?.from && dateRange?.to
    ? `${format(dateRange.from, 'dd MMM yyyy')}  -  ${format(dateRange.to, 'dd MMM yyyy')}`
    : 'Select date range';

  return (
    <div data-testid="analytics-date-range-picker">
      <Popover open={open} onOpenChange={handleOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-8 gap-1.5 font-normal"
            data-testid="analytics-custom-range-btn"
          >
            <CalendarDays size={14} />
            <span>{rangeLabel}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <div className="p-2 pb-0 text-center">
            <p className="text-xs text-muted-foreground">
              {clickCount.current === 0 ? 'Click a start date' : 'Click an end date'}
            </p>
          </div>
          <Calendar
            mode="range"
            selected={displayRange}
            onSelect={handleSelect}
            numberOfMonths={2}
            disabled={{ after: new Date() }}
            defaultMonth={subDays(new Date(), 30)}
            data-testid="analytics-calendar"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function ItemCombobox({ value, onSelect, placeholder, testId, suggestions }) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');

  useEffect(() => { setInputValue(value || ''); }, [value]);

  const filtered = (suggestions || []).filter(s =>
    s.toLowerCase().includes((inputValue || '').toLowerCase())
  );

  const handleSelect = (item) => {
    onSelect(item);
    setInputValue(item);
    setOpen(false);
  };

  const handleClear = () => {
    onSelect('');
    setInputValue('');
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSelect(inputValue);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative w-full sm:w-64">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => { setInputValue(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            className="pl-8 pr-8 h-8 text-sm"
            data-testid={testId}
          />
          {value && (
            <button onClick={handleClear} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid={`${testId}-clear`}>
              <X size={14} />
            </button>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command shouldFilter={false}>
          <CommandList>
            {filtered.length === 0 ? (
              <CommandEmpty className="py-3 text-center text-xs text-muted-foreground">
                {inputValue ? 'No matching items' : 'Type to search'}
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {filtered.map(item => (
                  <CommandItem key={item} value={item} onSelect={() => handleSelect(item)} className="text-sm cursor-pointer">
                    <Check size={14} className={`mr-2 ${value === item ? 'opacity-100' : 'opacity-0'}`} />
                    {item}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function AnalyticsPage() {
  const { user } = useAuth();
  const isAnalyst = user?.role === 'analyst';
  const [salesTrend, setSalesTrend] = useState([]);
  const [purchaseTrend, setPurchaseTrend] = useState([]);
  const [stockSummary, setStockSummary] = useState([]);
  const [topItems, setTopItems] = useState([]);
  const [supplierPerf, setSupplierPerf] = useState([]);
  const [stockMovement, setStockMovement] = useState([]);
  const [salesByItem, setSalesByItem] = useState([]);
  const [itemBoxMapping, setItemBoxMapping] = useState([]);
  const [salesByPurity, setSalesByPurity] = useState([]);
  const [dateRange, setDateRange] = useState({ from: subDays(new Date(), 29), to: new Date() });
  const [loading, setLoading] = useState(true);
  const [salesItemFilter, setSalesItemFilter] = useState('');
  const [itemMapFilter, setItemMapFilter] = useState('');
  const [allItemNames, setAllItemNames] = useState([]);
  const [topLimit, setTopLimit] = useState(10);

  // Fetch item names once for autocomplete suggestions
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const [itemsRes, boxesRes] = await Promise.all([
          API.get('/items'),
          API.get('/boxes?page=1&page_size=5000'),
        ]);
        const names = new Set();
        (itemsRes.data || []).forEach(i => { if (i.name) names.add(i.name); });
        const boxes = boxesRes.data?.boxes || boxesRes.data || [];
        boxes.forEach(b => (b.item_names || []).forEach(n => names.add(n)));
        setAllItemNames([...names].sort());
      } catch (e) { /* silent */ }
    };
    fetchItems();
  }, []);

  const fetchData = useCallback(async () => {
    if (!dateRange?.from || !dateRange?.to) return;
    setLoading(true);
    const params = `start_date=${toISO(dateRange.from)}&end_date=${toISO(dateRange.to)}`;
    try {
      const salesItemParams = salesItemFilter ? `${params}&item_name=${encodeURIComponent(salesItemFilter)}` : params;
      const itemMapParams = itemMapFilter ? `?item_name=${encodeURIComponent(itemMapFilter)}` : '';
      const [trendRes, stockRes, itemsRes, supplierRes, purchaseRes, movementRes, salesItemRes, itemMapRes, salesPurityRes] = await Promise.all([
        API.get(`/analytics/sales-trend?${params}`),
        API.get('/analytics/stock-summary'),
        API.get(`/analytics/top-items?${params}&limit=${topLimit}`),
        API.get(`/analytics/supplier-performance?${params}`),
        API.get(`/analytics/purchase-trend?${params}`),
        API.get(`/analytics/stock-movement?${params}`),
        API.get(`/analytics/sales-by-item?${salesItemParams}`),
        API.get(`/analytics/item-box-mapping${itemMapParams}`),
        API.get(`/analytics/sales-by-purity?${params}`),
      ]);
      setSalesTrend(trendRes.data);
      setStockSummary(stockRes.data);
      setTopItems(itemsRes.data);
      setSupplierPerf(supplierRes.data);
      setPurchaseTrend(purchaseRes.data);
      setStockMovement(movementRes.data);
      setSalesByItem(salesItemRes.data);
      setItemBoxMapping(itemMapRes.data);
      setSalesByPurity(salesPurityRes.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [dateRange, salesItemFilter, itemMapFilter, topLimit]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatTick = (d) => {
    const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `${m[3]}/${m[2]}`;
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit' }); }
    catch { return d; }
  };

  const rangeLabelShort = dateRange?.from && dateRange?.to
    ? `${format(dateRange.from, 'dd MMM')} - ${format(dateRange.to, 'dd MMM yyyy')}`
    : '';

  const totalSalesG = salesTrend.reduce((acc, d) => acc + (d.total_sales_g || 0), 0);
  const totalPurchaseG = purchaseTrend.reduce((acc, d) => acc + (d.total_purchase_g || 0), 0);
  const totalStockG = stockSummary.reduce((acc, s) => acc + (s.total_gold_g || 0), 0);

  return (
    <div data-testid="analytics-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1>Analytics</h1>
          <p className="hidden sm:block text-sm text-muted-foreground">Advanced business insights and trends</p>
        </div>
        <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {!isAnalyst && (
        <Card className="card-hover">
          <CardContent className="pt-4 px-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Net Sales</p>
                <p className="kpi-number text-foreground" data-testid="analytics-total-sales">{formatWeight(totalSalesG)}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{rangeLabelShort}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-950/40 flex items-center justify-center"><TrendingUp className="text-emerald-400" size={18} /></div>
            </div>
          </CardContent>
        </Card>
        )}
        <Card className="card-hover">
          <CardContent className="pt-4 px-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Net Purchases</p>
                <p className="kpi-number text-foreground" data-testid="analytics-total-purchases">{formatWeight(totalPurchaseG)}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{rangeLabelShort}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-blue-950/40 flex items-center justify-center"><TrendingDown className="text-blue-400" size={18} /></div>
            </div>
          </CardContent>
        </Card>
        {!isAnalyst && (
        <Card className="card-hover">
          <CardContent className="pt-4 px-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Current Stock</p>
                <p className="kpi-number text-foreground" data-testid="analytics-total-stock">{formatWeight(totalStockG)}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-950/40 flex items-center justify-center"><Package className="text-amber-400" size={18} /></div>
            </div>
          </CardContent>
        </Card>
        )}
        <Card className="card-hover">
          <CardContent className="pt-4 px-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Suppliers Active</p>
                <p className="kpi-number text-foreground" data-testid="analytics-supplier-count">{supplierPerf.length}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{rangeLabelShort}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-rose-950/40 flex items-center justify-center"><Truck className="text-rose-400" size={18} /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="w-full sm:w-auto flex overflow-x-auto mb-4">
          <TabsTrigger value="overview" className="text-xs sm:text-sm whitespace-nowrap">Overview</TabsTrigger>
          <TabsTrigger value="sales-report" className="text-xs sm:text-sm whitespace-nowrap">Sales Report</TabsTrigger>
          <TabsTrigger value="item-mapping" className="text-xs sm:text-sm whitespace-nowrap" data-testid="analytics-item-mapping-tab">Item Mapping</TabsTrigger>
          <TabsTrigger value="suppliers" className="text-xs sm:text-sm whitespace-nowrap">Suppliers</TabsTrigger>
          {!isAnalyst && <TabsTrigger value="movement" className="text-xs sm:text-sm whitespace-nowrap">Stock Movement</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {!isAnalyst && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BarChart3 size={16} />Sales Trend<span className="text-[10px] font-normal text-muted-foreground">(Sales − Returns)</span></CardTitle></CardHeader>
              <CardContent className="px-2">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={salesTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke={axisStroke} />
                    <XAxis dataKey="date" tick={chartTickStyle} tickFormatter={formatTick} />
                    <YAxis tick={chartTickStyle} width={30} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="total_sales_g" fill="hsl(40, 89%, 45%)" radius={[4, 4, 0, 0]} name="Net Sales (g)" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            )}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingDown size={16} />Purchase Trend<span className="text-[10px] font-normal text-muted-foreground">(Purchases − Returns)</span></CardTitle></CardHeader>
              <CardContent className="px-2">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={purchaseTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke={axisStroke} />
                    <XAxis dataKey="date" tick={chartTickStyle} tickFormatter={formatTick} />
                    <YAxis tick={chartTickStyle} width={30} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="total_purchase_g" fill="hsl(220, 32%, 32%)" radius={[4, 4, 0, 0]} name="Net Purchase (g)" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {!isAnalyst && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Stock by Purity</CardTitle></CardHeader>
              <CardContent className="px-2">
                {stockSummary.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={stockSummary.map(s => ({ name: `Purity ${s.purity}`, value: s.total_gold_g, purity: s.purity }))} cx="50%" cy="50%" innerRadius={50} outerRadius={85} dataKey="value"
                        label={({ name, value }) => `${name}: ${value}g`} labelLine={{ strokeWidth: 1 }}>
                        {stockSummary.map((s, i) => <Cell key={i} fill={getPurityColor(s.purity)} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-center py-12 text-muted-foreground text-sm">No stock data</p>}
              </CardContent>
            </Card>
            )}
            {!isAnalyst && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Sales by Purity<span className="ml-2 text-[10px] font-normal text-muted-foreground">(Sales − Returns)</span></CardTitle></CardHeader>
              <CardContent className="px-2">
                {salesByPurity.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={salesByPurity.map(s => ({ name: `Purity ${s.purity}`, value: s.total_sold_g, count: s.sale_count, purity: s.purity }))} cx="50%" cy="50%" innerRadius={50} outerRadius={85} dataKey="value"
                        label={({ name, value }) => `${name}: ${value}g`} labelLine={{ strokeWidth: 1 }}>
                        {salesByPurity.map((s, i) => <Cell key={i} fill={getPurityColor(s.purity)} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-center py-12 text-muted-foreground text-sm">No sales data for period</p>}
              </CardContent>
            </Card>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">Top Selling Boxes<span className="ml-2 text-[10px] font-normal text-muted-foreground">(Sales − Returns)</span></CardTitle>
                  <Select value={String(topLimit)} onValueChange={v => setTopLimit(Number(v))}>
                    <SelectTrigger className="h-8 w-[110px] text-xs" data-testid="top-boxes-limit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 20, 30, 40, 50, 60, 70].map(n => (
                        <SelectItem key={n} value={String(n)} data-testid={`top-boxes-limit-${n}`}>Top {n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {topItems.length > 0 ? (
                  <div className="space-y-2" data-testid="top-boxes-list">
                    {topItems.map((item, i) => (
                      <div key={item.box_id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-semibold text-xs sm:text-sm">{item.box_id}</span>
                              {item.purity && <span className={`status-badge purity-${item.purity} text-[10px]`}>{item.purity}</span>}
                            </div>
                            {item.description && <p className="text-xs text-muted-foreground truncate mt-0.5">{item.description}</p>}
                            {item.item_names?.length > 0 && <p className="text-[10px] text-muted-foreground/70 truncate">{item.item_names.join(', ')}</p>}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-3">
                          <p className="font-mono font-semibold text-xs sm:text-sm" data-testid={`top-box-net-${item.box_id}`}>{formatWeight(item.total_sold_g)}</p>
                          <p className="text-xs text-muted-foreground">{item.sale_count} sales{item.return_count ? ` · ${item.return_count} ret` : ''}</p>
                          {item.return_count ? <p className="text-[10px] text-muted-foreground/70">{formatWeight(item.gross_sold_g)} − {formatWeight(item.returns_g)}</p> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-center py-12 text-muted-foreground text-sm">No sales data</p>}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="sales-report">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="text-base">Sales by Item ({rangeLabelShort})<span className="ml-2 text-[10px] font-normal text-muted-foreground">(Sales − Returns)</span></CardTitle>
                <ItemCombobox
                  value={salesItemFilter}
                  onSelect={setSalesItemFilter}
                  placeholder="Search item name..."
                  testId="sales-item-search"
                  suggestions={allItemNames}
                />
              </div>
            </CardHeader>
            <CardContent>
              {salesByItem.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/60">
                        <th className="text-left py-2 px-3 font-semibold">Item Name</th>
                        <th className="text-right py-2 px-3 font-semibold"><span className="status-badge purity-76 text-[10px]">76</span></th>
                        <th className="text-right py-2 px-3 font-semibold"><span className="status-badge purity-84 text-[10px]">84</span></th>
                        <th className="text-right py-2 px-3 font-semibold"><span className="status-badge purity-92 text-[10px]">92</span></th>
                        <th className="text-right py-2 px-3 font-semibold" style={{ color: 'hsl(43, 96%, 56%)' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesByItem.map((item) => (
                        <tr key={item.item_name} className="border-b border-border/20 hover:bg-muted/30 transition-colors" data-testid={`sales-item-row-${item.item_name}`}>
                          <td className="py-2.5 px-3 font-medium">{item.item_name}</td>
                          <td className="py-2.5 px-3 text-right font-mono text-xs">{item.purity_totals?.["76"] > 0 ? formatWeight(item.purity_totals["76"]) : '—'}</td>
                          <td className="py-2.5 px-3 text-right font-mono text-xs">{item.purity_totals?.["84"] > 0 ? formatWeight(item.purity_totals["84"]) : '—'}</td>
                          <td className="py-2.5 px-3 text-right font-mono text-xs">{item.purity_totals?.["92"] > 0 ? formatWeight(item.purity_totals["92"]) : '—'}</td>
                          <td className="py-2.5 px-3 text-right font-mono font-semibold" style={{ color: 'hsl(43, 96%, 56%)' }}>{formatWeight(item.total_sold_g)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      {!isAnalyst && (
                      <tr className="border-t-2 border-border/60 font-semibold">
                        <td className="py-2.5 px-3">Grand Total</td>
                        <td className="py-2.5 px-3 text-right font-mono text-xs">{formatWeight(salesByItem.reduce((s, i) => s + (i.purity_totals?.["76"] || 0), 0))}</td>
                        <td className="py-2.5 px-3 text-right font-mono text-xs">{formatWeight(salesByItem.reduce((s, i) => s + (i.purity_totals?.["84"] || 0), 0))}</td>
                        <td className="py-2.5 px-3 text-right font-mono text-xs">{formatWeight(salesByItem.reduce((s, i) => s + (i.purity_totals?.["92"] || 0), 0))}</td>
                        <td className="py-2.5 px-3 text-right font-mono" style={{ color: 'hsl(43, 96%, 56%)' }}>{formatWeight(salesByItem.reduce((s, i) => s + i.total_sold_g, 0))}</td>
                      </tr>
                      )}
                    </tfoot>
                  </table>
                </div>
              ) : <p className="text-center py-12 text-muted-foreground text-sm">{salesItemFilter ? 'No matching items found' : 'No sales data for this period'}</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="item-mapping">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="text-base">Item-Box Mapping</CardTitle>
                <ItemCombobox
                  value={itemMapFilter}
                  onSelect={setItemMapFilter}
                  placeholder="Search item name..."
                  testId="item-map-search"
                  suggestions={allItemNames}
                />
              </div>
            </CardHeader>
            <CardContent>
              {itemBoxMapping.length > 0 ? (
                <div className="space-y-4">
                  {itemBoxMapping.map(item => (
                    <div key={item.item_id || item.item_name} className="rounded-lg border bg-card overflow-hidden" data-testid={`item-mapping-${item.item_name}`}>
                      <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2" style={{ background: 'hsl(30, 8%, 11%)' }}>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold">{item.item_name}</span>
                          <span className="text-xs text-muted-foreground">{item.box_count} {item.box_count === 1 ? 'box' : 'boxes'}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-muted-foreground">Gold: <b className="weight-value" style={{ color: 'hsl(43, 96%, 56%)' }}>{formatWeight(item.total_gold_g)}</b></span>
                          <span className="text-muted-foreground">Gross: <b className="weight-value">{formatWeight(item.total_gross_g)}</b></span>
                        </div>
                      </div>
                      {/* Purity subtotals */}
                      {item.purity_subtotals && Object.keys(item.purity_subtotals).length > 0 && (
                        <div className="px-4 py-2 flex flex-wrap gap-3 border-b border-border/30 bg-muted/20">
                          {['76', '84', '92'].map(p => {
                            const sub = item.purity_subtotals[p];
                            if (!sub) return null;
                            return (
                              <div key={p} className="flex items-center gap-1.5 text-xs">
                                <span className={`status-badge purity-${p} text-[10px]`}>{p}</span>
                                <span>{sub.box_count} box{sub.box_count !== 1 ? 'es' : ''}</span>
                                <span className="text-muted-foreground">|</span>
                                <span>Gold: <b style={{ color: 'hsl(43, 96%, 56%)' }}>{formatWeight(sub.total_gold_g)}</b></span>
                                <span>Gross: <b>{formatWeight(sub.total_gross_g)}</b></span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {item.boxes.length > 0 ? (
                        <div className="divide-y divide-border/40">
                          {item.boxes.map(box => (
                            <div key={box.box_id} className="px-4 py-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 hover:bg-muted/20 transition-colors">
                              <div className="flex items-center gap-2 min-w-[120px]">
                                <span className="font-mono text-sm font-semibold">{box.box_id}</span>
                                <span className={`status-badge purity-${box.gold_purity} text-[10px]`}>{box.gold_purity}</span>
                              </div>
                              {box.description && <span className="text-xs text-muted-foreground truncate max-w-[200px]">{box.description}</span>}
                              <div className="flex gap-3 sm:ml-auto text-xs">
                                <span>Gross: <b className="weight-value">{formatWeight(box.gross_weight_g)}</b></span>
                                <span>Gold: <b className="weight-value" style={{ color: 'hsl(43, 96%, 56%)' }}>{formatWeight(box.gold_weight_g)}</b></span>
                                <span>Poly: <b className="weight-value">{formatWeight(box.polyethene_weight_g)}</b></span>
                                <span>Box: <b className="weight-value">{formatWeight(box.box_weight_g)}</b></span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="px-4 py-3 text-xs text-muted-foreground">Not present in any active box</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : <p className="text-center py-12 text-muted-foreground text-sm">{itemMapFilter ? 'No matching items found' : 'No items found. Add items from the Item Names page first.'}</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suppliers">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Supplier Purchase Volume</CardTitle></CardHeader>
              <CardContent className="px-2">
                {supplierPerf.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={supplierPerf} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke={axisStroke} />
                      <XAxis type="number" tick={chartTickStyle} />
                      <YAxis type="category" dataKey="supplier" tick={chartTickStyle} width={100} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="total_purchase_g" fill="hsl(40, 89%, 45%)" radius={[0, 4, 4, 0]} name="Purchase (g)" />
                      <Bar dataKey="total_return_g" fill="hsl(0, 72%, 51%)" radius={[0, 4, 4, 0]} name="Return (g)" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-center py-12 text-muted-foreground text-sm">No supplier data</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Supplier Summary</CardTitle></CardHeader>
              <CardContent>
                {supplierPerf.length > 0 ? (
                  <div className="space-y-2">
                    {supplierPerf.map((s) => (
                      <div key={s.supplier} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <div>
                          <p className="font-semibold text-sm">{s.supplier}</p>
                          <p className="text-xs text-muted-foreground">{s.purchase_count} purchases, {s.return_count} returns</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-semibold text-sm">{formatWeight(s.net_purchase_g)}</p>
                          <p className="text-xs text-muted-foreground">net</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-center py-12 text-muted-foreground text-sm">No supplier data</p>}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {!isAnalyst && (
        <TabsContent value="movement">
          <div className="grid grid-cols-1 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ArrowUpDown size={16} />Daily Stock Movement</CardTitle></CardHeader>
              <CardContent className="px-2">
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={stockMovement}>
                    <CartesianGrid strokeDasharray="3 3" stroke={axisStroke} />
                    <XAxis dataKey="date" tick={chartTickStyle} tickFormatter={formatTick} />
                    <YAxis tick={chartTickStyle} width={40} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="inflow_g" fill="hsl(160, 45%, 34%)" radius={[4, 4, 0, 0]} name="Inflow (g)" />
                    <Bar dataKey="outflow_g" fill="hsl(0, 72%, 51%)" radius={[4, 4, 0, 0]} name="Outflow (g)" />
                    <Line type="monotone" dataKey="net_g" stroke="hsl(40, 89%, 45%)" strokeWidth={2} dot={{ r: 2 }} name="Net (g)" />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Sales vs Purchases</CardTitle></CardHeader>
              <CardContent className="px-2">
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={salesTrend.map((s, i) => ({
                    date: s.date,
                    sales_g: s.total_sales_g,
                    purchase_g: purchaseTrend[i]?.total_purchase_g || 0,
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke={axisStroke} />
                    <XAxis dataKey="date" tick={chartTickStyle} tickFormatter={formatTick} />
                    <YAxis tick={chartTickStyle} width={40} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Area type="monotone" dataKey="sales_g" stroke="hsl(40, 89%, 45%)" fill="hsl(40, 89%, 45%)" fillOpacity={0.15} name="Sales (g)" />
                    <Area type="monotone" dataKey="purchase_g" stroke="hsl(220, 32%, 32%)" fill="hsl(220, 32%, 32%)" fillOpacity={0.15} name="Purchase (g)" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
